import { Component, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  Store,
  Phone,
  Lock,
  ArrowRight,
  User as UserIcon,
  Eye,
  EyeOff,
  Check,
  TrendingUp,
  ShieldCheck,
  Boxes,
  Sparkles,
} from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../core/services/auth.service';
import { LoginRequest } from '../../core/models/auth.models';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    LucideAngularModule,
    TranslatePipe,
    DialogModule,
    ButtonModule,
  ],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  // ── Parolni tiklash — ikki bosqichli BITTA oqim ────────────────────────────
  //
  // Ilgari ikkita alohida oyna bor edi: biri kod so'raydigan, ikkinchisi kodni
  // kiritadigan. Kod so'raydiganini ochadigan tugma esa umuman yo'q edi
  // (`openForgot` hech qayerdan chaqirilmasdi), shuning uchun foydalanuvchi
  // kodni qayerdan olishini bilmay, uni kiritish oynasiga tushib qolardi.
  resetOpen = signal(false);
  /** `false` — telefon so'raladi, `true` — kod va yangi parol so'raladi. */
  otpSent = signal(false);
  sendingOtp = signal(false);
  resetting = signal(false);
  resetMsg = signal<string | null>(null);
  resetErr = signal<string | null>(null);

  resetOtp = '';
  resetPassword = '';
  resetPasswordConfirm = '';

  /**
   * Parol yangilandi — oyna yopilgandan keyin kirish formasi ustida ko'rinadi.
   * Matn serverdan kelishi mumkin; kelmasa shablon tarjimadan sukut matnni oladi,
   * shuning uchun bayroq va matn alohida.
   */
  resetDone = signal(false);
  resetDoneMsg = signal<string | null>(null);

  loginData: LoginRequest = {
    phoneNumber: '',
    password: '',
  };

  Store = Store;
  Phone = Phone;
  Lock = Lock;
  ArrowRight = ArrowRight;
  UserIcon = UserIcon;
  Eye = Eye;
  EyeOff = EyeOff;
  Check = Check;
  TrendingUp = TrendingUp;
  ShieldCheck = ShieldCheck;
  Boxes = Boxes;
  Sparkles = Sparkles;

  showPassword = signal(false);
  showResetPassword = signal(false);

  phoneCode = '+998';
  localPhone = '';
  resetPhoneCode = '+998';
  resetLocalPhone = '';

  /** Backend `ResetPasswordCommand` da ham shu chegara tekshiriladi. */
  private static readonly MinPasswordLength = 6;

  // 1. Sahifa yuklanishi bilan token bo'lsa, avtomatik ichkariga otib yuboramiz
  ngOnInit(): void {
    if (this.authService.isLoggedIn?.() || this.authService.user?.()) {
      this.redirectUserBasedOnRole();
    }
  }

  handleLogin(event: Event) {
    event.preventDefault();
    this.errorMessage.set(null);
    this.isLoading.set(true);
    this.loginData.phoneNumber = this.phoneCode + this.localPhone.trim();

    this.authService
      .login(this.loginData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (!res.success || !res.data) {
            this.errorMessage.set(res.errors?.join(' ') ?? 'Login failed');
            return;
          }
          // 2. Muvaffaqiyatli kirilganda replaceUrl: true bilan yo'naltiramiz
          this.redirectUserBasedOnRole();
        },
        error: (err) => {
          this.isLoading.set(false);
          const msg =
            err.error?.errors?.join?.(' ') ??
            err.error?.message ??
            'Login failed. Please check your credentials.';
          this.errorMessage.set(msg);
        },
      });
  }

  // Yo'naltirish mantigi (replaceUrl: true qo'shilgan)
  private redirectUserBasedOnRole(): void {
    const role = this.authService.user()?.role;

    if (this.authService.pendingBusinessCount() > 1) {
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/app';
      void this.router.navigate(['/select-business'], {
        queryParams: { returnUrl },
        replaceUrl: true, // Loginni tarixdan o'chiradi
      });
      return;
    }

    if (role === 'SuperAdmin' || role === 'Admin' || role === 'Operator') {
      void this.router.navigate(['/admin'], { replaceUrl: true });
    } else {
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/app';
      void this.router.navigateByUrl(returnUrl, { replaceUrl: true });
    }
  }

  /** Oyna har doim 1-bosqichdan ochiladi — kirish formasidagi telefon oldindan qo'yiladi. */
  openReset() {
    this.resetPhoneCode = this.phoneCode;
    this.resetLocalPhone = this.localPhone;
    this.resetOtp = '';
    this.resetPassword = '';
    this.resetPasswordConfirm = '';
    this.otpSent.set(false);
    this.resetMsg.set(null);
    this.resetErr.set(null);
    this.showResetPassword.set(false);
    this.resetDone.set(false); // oldingi tiklashning xabari qolib ketmasin
    this.resetOpen.set(true);
  }

  private get resetPhone(): string {
    return this.resetPhoneCode + this.resetLocalPhone.trim();
  }

  canSendOtp(): boolean {
    return !this.sendingOtp() && this.resetLocalPhone.trim().length > 0;
  }

  /** 1-bosqich: kod so'rash. Muvaffaqiyatli bo'lsa oyna 2-bosqichga o'tadi. */
  sendOtp() {
    if (!this.canSendOtp()) return;

    this.resetErr.set(null);
    this.resetMsg.set(null);
    this.sendingOtp.set(true);

    this.authService
      .forgotPassword({ phoneNumber: this.resetPhone })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.sendingOtp.set(false);
          if (!res.success) {
            this.resetErr.set(res.errors?.join(' ') ?? 'Error');
            return;
          }
          this.otpSent.set(true);
          this.resetMsg.set((res.data as { message?: string })?.message ?? null);
        },
        error: (err) => {
          this.sendingOtp.set(false);
          this.resetErr.set(err.error?.errors?.join?.(' ') ?? err.message ?? 'Error');
        },
      });
  }

  /** 2-bosqichdan orqaga — telefonni tuzatish yoki kodni qayta so'rash uchun. */
  backToPhoneStep() {
    this.otpSent.set(false);
    this.resetOtp = '';
    this.resetMsg.set(null);
    this.resetErr.set(null);
  }

  passwordsMismatch(): boolean {
    return this.resetPasswordConfirm.length > 0 && this.resetPassword !== this.resetPasswordConfirm;
  }

  canReset(): boolean {
    return (
      !this.resetting() &&
      this.resetOtp.trim().length > 0 &&
      this.resetPassword.length >= LoginComponent.MinPasswordLength &&
      this.resetPassword === this.resetPasswordConfirm
    );
  }

  /** 2-bosqich: kod va yangi parol bilan parolni yangilash. */
  submitReset() {
    if (!this.canReset()) return;

    this.resetErr.set(null);
    this.resetMsg.set(null);
    this.resetting.set(true);

    this.authService
      .resetPassword({
        phoneNumber: this.resetPhone,
        otpCode: this.resetOtp.trim(),
        newPassword: this.resetPassword,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.resetting.set(false);
          if (!res.success) {
            this.resetErr.set(res.errors?.join(' ') ?? 'Error');
            return;
          }

          // Oyna yopiladi va telefon kirish formasiga ko'chiriladi — foydalanuvchi
          // darhol yangi parol bilan kirishi mumkin.
          this.resetOpen.set(false);
          this.phoneCode = this.resetPhoneCode;
          this.localPhone = this.resetLocalPhone.trim();
          this.loginData.password = '';
          this.errorMessage.set(null);
          this.resetDoneMsg.set((res.data as { message?: string })?.message ?? null);
          this.resetDone.set(true);
        },
        error: (err) => {
          this.resetting.set(false);
          this.resetErr.set(err.error?.errors?.join?.(' ') ?? err.message ?? 'Error');
        },
      });
  }
}
